'use client'
import { LinePlus } from '@qr-front/icons/line-plus'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { InputFieldEmailForm } from 'features/auth/components/InputFieldEmailForm'
import { useAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useFormContext } from 'react-hook-form'

import { useSyncFieldWithAtom } from '@/hooks'
import { removeAtIndex } from '@/utils'

import { InputButton } from '../../InputButton'
import { ContactEntryRow } from './ContactEntryRow'
import {
	capCustomLabel,
	createEntry,
	EMAIL_TYPE_OPTIONS,
	TContactEntry,
	TContactType,
} from './contactEntryTypes'

export const contactInfoEmailListAtom = atomWithReset<TContactEntry[]>([
	createEntry(),
])

export const EmailFields = ({ path }: { path: string }) => {
	const t = useTranslations()
	const { getValues, setFocus, setValue, trigger } = useFormContext()

	const [emailList, setEmailList] = useAtom(contactInfoEmailListAtom)

	useSyncFieldWithAtom({
		atom: contactInfoEmailListAtom,
		setFieldValue: (value) =>
			setValue(
				path,
				value.map((entry) => ({ value: entry.value })),
			),
	})

	const handleClickAdd = () => {
		setEmailList((prev) => [...prev, createEntry()])
		trigger(path).then(() =>
			setFocus(`${path}.${getValues(path)!.length - 1}.value`),
		)
	}

	const handleClickRemove = (index: number) => {
		setEmailList((prev) => removeAtIndex(prev, index))
		trigger(path)
	}

	const handleValueChange = (index: number, value: string) => {
		setEmailList((prev) =>
			prev.map((entry, i) => (i === index ? { ...entry, value } : entry)),
		)
	}

	const handleTypeChange = (index: number, type: TContactType) => {
		setEmailList((prev) =>
			prev.map((entry, i) => {
				if (i !== index) return entry
				const { label: _label, ...rest } = entry
				return type === 'custom'
					? { ...rest, type, label: '' }
					: { ...rest, type }
			}),
		)
	}

	const handleLabelChange = (index: number, label: string) => {
		setEmailList((prev) =>
			prev.map((entry, i) =>
				i === index
					? { ...entry, label: capCustomLabel(label) }
					: entry,
			),
		)
	}

	const groupLabel = t(
		'generator__content_form_section__about__contact_section__email',
	)

	return (
		<>
			{emailList.map((entry, index) => (
				<ContactEntryRow
					key={index}
					entry={entry}
					options={EMAIL_TYPE_OPTIONS}
					fieldId={`${path}-${index}-type`}
					groupLabel={groupLabel}
					showGroupLabel={index === 0}
					onTypeChange={(type) => handleTypeChange(index, type)}
					onLabelChange={(label) => handleLabelChange(index, label)}
					action={
						index === 0 ? (
							<InputButton
								icon={<LinePlus />}
								onClick={handleClickAdd}
								ariaLabel={t(
									'generator__content_form_section__about__contact_section__add_entry',
								)}
							/>
						) : (
							<InputButton
								icon={<LineTrashAlt className="size-5" />}
								onClick={() => handleClickRemove(index)}
								ariaLabel={t(
									'generator__content_form_section__about__contact_section__remove_entry',
								)}
							/>
						)
					}
				>
					<InputFieldEmailForm
						ariaLabel={groupLabel}
						name={`${path}.${index}.value`}
						value={entry.value}
						onChange={(value) => handleValueChange(index, value)}
						className="grow"
						placeholder={t(
							'generator__content_form_section__about__contact_section__email__placeholder',
						)}
						leftIcon={null}
					/>
				</ContactEntryRow>
			))}
		</>
	)
}
